import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DateRangePicker from '../components/DateRangePicker';
import InspectorHoverCard from '../components/InspectorHoverCard';
import { useAuth } from '../contexts/AuthContext';

interface FabricInspectionRecord {
  id: string;
  poNumber: string;
  inspectionBasis?: string;
  supplier: string;
  date: string;
  rawDate: string; // ISO string for sorting/filtering
  inspector: {
    name: string;
    initials: string;
    color: string;
    photo?: string;
    inspectorId: string | null;
  };
  status: 'Pass' | 'Fail' | 'Pending';
}

const AVATAR_COLORS = [
  'bg-slate-200 text-slate-600',
  'bg-blue-100 text-blue-600',
  'bg-orange-100 text-orange-600',
  'bg-emerald-100 text-emerald-600',
  'bg-purple-100 text-purple-600',
];

const getAvatarColor = (id: string) => {
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const FabricInspectionReports: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [reports, setReports] = useState<FabricInspectionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState('All');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;


  useEffect(() => {
    const fetchReports = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const res = await fetch(`${baseUrl}/api/v1/fabric-inspections`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const json = await res.json();
        if (json.success) {
          const mapped: FabricInspectionRecord[] = json.data.map((r: any) => ({
            id: r.id,
            poNumber: r.po_number || '-',
            inspectionBasis: r.inspection_basis || 'PO',
            supplier: r.supplierName || r.supplier || 'N/A',
            date: new Date(r.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }),
            rawDate: r.created_at,
            inspector: {
              name: r.inspector?.name || r.inspector_name || 'N/A',
              initials: (r.inspector?.name || r.inspector_name || 'U').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase(),
              color: getAvatarColor(r.inspector?.id || r.id),
              photo: r.inspector?.profileImage || undefined,
              inspectorId: r.inspector?.id || null,
            },
            status: r.result === 'PASS' ? 'Pass' : r.result === 'FAIL' ? 'Fail' : 'Pending',
          }));
          setReports(mapped);
        }
      } catch (err) {
        console.error('Failed to fetch fabric reports:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchReports();
    }
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  type UserObj = { name: string; photo?: string; color: string; initials: string; inspectorId?: string | null };
  const uniqueUserObjects: UserObj[] = [
    { name: 'All', photo: undefined, color: '', initials: '' },
    ...(Array.from(new Map(reports.map((r: FabricInspectionRecord) => [r.inspector.name, r.inspector])).values()) as UserObj[]),
  ];

  const filteredReports = reports.filter(r => {
    const matchesStatus = activeFilter === 'All' || r.status === activeFilter;
    const matchesUser = selectedUser === 'All' || r.inspector.name === selectedUser;

    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      r.id.toLowerCase().includes(q) ||
      r.supplier.toLowerCase().includes(q) ||
      r.inspector.name.toLowerCase().includes(q);

    const reportDate = new Date(r.rawDate);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);

    const matchesDate = (!from || reportDate >= from) && (!to || reportDate <= to);

    return matchesStatus && matchesSearch && matchesDate && matchesUser;
  });


  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter, selectedUser, dateFrom, dateTo]);

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedReports = filteredReports.slice(indexOfFirstItem, indexOfLastItem);


  const getStatusBadge = (status: FabricInspectionRecord['status']) => {
    switch (status) {
      case 'Pass':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-600"></span>
            Pass
          </span>
        );
      case 'Fail':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
            <span className="h-1.5 w-1.5 rounded-full bg-red-600"></span>
            Fail
          </span>
        );
      case 'Pending':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-600/10">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-500"></span>
            Pending
          </span>
        );
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Fabric Inspection Report</h1>
        <p className="text-sm text-slate-500">Manage fabric inspection reports and outcomes</p>
      </div>

      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 w-full md:flex-row md:items-center">
          {/* Search Box */}
          <div className="relative w-full md:max-w-md">
            <input
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm placeholder-slate-400 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] outline-none"
              placeholder="Search by Inspection ID or Supplier..."
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="material-symbols-outlined absolute left-3 top-2.5 text-[20px] text-slate-400">search</span>
          </div>

          {/* User Filter */}
          <div className="relative inline-block" ref={userDropdownRef}>
            <button
              onClick={() => setIsUserDropdownOpen((prev) => !prev)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer min-w-[140px]"
            >
              {selectedUser === 'All' ? (
                <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-[14px] text-slate-500">group</span>
                </span>
              ) : (() => {
                const u = uniqueUserObjects.find(x => x.name === selectedUser);
                return u?.photo ? (
                  <img src={u.photo} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                ) : (
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u?.color || 'bg-slate-200 text-slate-600'}`}>
                    {u?.initials || selectedUser[0]}
                  </span>
                );
              })()}
              <span className="truncate">{selectedUser === 'All' ? 'All Users' : selectedUser}</span>
              <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
            </button>
            {isUserDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 z-20 min-w-[180px] rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                {uniqueUserObjects.map(u => (
                  <button
                    key={u.name}
                    onClick={() => { setSelectedUser(u.name); setIsUserDropdownOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedUser === u.name ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                  >
                    {u.name === 'All' ? (
                      <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-[14px] text-slate-500">group</span>
                      </span>
                    ) : u.photo ? (
                      <img src={u.photo} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                    ) : (
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u.color}`}>
                        {u.initials}
                      </span>
                    )}
                    {u.name === 'All' ? 'All Users' : u.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Date Picker */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-medium text-slate-700">Filter Date:</span>
          <DateRangePicker
            range={{ from: dateFrom, to: dateTo }}
            onChange={(range) => {
              setDateFrom(range.from);
              setDateTo(range.to);
            }}
            onClear={() => {
              setDateFrom('');
              setDateTo('');
            }}
          />
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {['All', 'Pass', 'Fail', 'Pending'].map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`rounded-lg px-5 py-2.5 text-sm font-medium transition-all shadow-sm ${activeFilter === filter
              ? 'bg-[#136bce] text-white shadow-blue-500/30'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
          >
            {filter}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-6 py-4 font-semibold tracking-wide border-b border-slate-200">PO / Batch No.</th>
                <th className="px-6 py-4 font-semibold tracking-wide border-b border-slate-200">Supplier</th>
                <th className="px-6 py-4 font-semibold tracking-wide border-b border-slate-200 text-center">Date</th>
                <th className="px-6 py-4 font-semibold tracking-wide border-b border-slate-200">Inspector</th>
                <th className="px-6 py-4 font-semibold tracking-wide border-b border-slate-200">Status</th>
                <th className="px-6 py-4 border-b border-slate-200 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#136bce]"></div>
                      Loading inspection reports...
                    </div>
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    No inspection reports found.
                  </td>
                </tr>
              ) : (
                paginatedReports.map((report) => (
                  <tr
                    key={report.id}
                    className="group transition-colors hover:bg-blue-50/30 cursor-pointer"
                    onClick={() => navigate(`/inspection-report/${report.id}`)}
                  >

                    <td className="px-6 py-4 align-middle text-slate-900 font-medium whitespace-nowrap">
                      <div className="flex flex-col">
                        <span>{report.poNumber}</span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider">{report.inspectionBasis === 'Batch' ? 'Batch' : 'PO'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle text-slate-700 whitespace-nowrap">{report.supplier}</td>
                    <td className="px-6 py-4 align-middle text-slate-500 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="material-symbols-outlined text-[16px] text-slate-400">event</span>
                        {report.date}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-middle text-slate-700 whitespace-nowrap">
                      <InspectorHoverCard
                        inspectorId={report.inspector.inspectorId}
                        name={report.inspector.name}
                        initials={report.inspector.initials}
                        color={report.inspector.color}
                        photo={report.inspector.photo}
                        token={token || ''}
                      >
                        <div className="flex items-center gap-2">
                          {report.inspector.photo ? (
                            <img
                              src={report.inspector.photo}
                              alt={report.inspector.name}
                              className="h-6 w-6 rounded-full object-cover border border-slate-200"
                              onError={(e: any) => {
                                e.currentTarget.style.display = 'none';
                                const sib = e.currentTarget.nextElementSibling as HTMLElement;
                                if (sib) sib.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${report.inspector.color}`}
                            style={{ display: report.inspector.photo ? 'none' : 'flex' }}
                          >
                            {report.inspector.initials}
                          </div>
                          {report.inspector.name}
                        </div>
                      </InspectorHoverCard>
                    </td>
                    <td className="px-6 py-4 align-middle whitespace-nowrap">{getStatusBadge(report.status)}</td>
                    <td className="px-6 py-4 align-middle text-right">
                      <span className="material-symbols-outlined text-slate-300 group-hover:text-[#136bce] transition-colors">chevron_right</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">
          <p className="text-sm text-slate-500">
            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredReports.length)}</span> of <span className="font-medium">{filteredReports.length}</span> results
          </p>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                // Show first, last, and pages around current
                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
              })
              .map((page, index, array) => (
                <React.Fragment key={page}>
                  {index > 0 && array[index - 1] !== page - 1 && (
                    <div className="flex h-8 w-8 items-center justify-center text-slate-400">...</div>
                  )}
                  <button
                    onClick={() => setCurrentPage(page)}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors ${currentPage === page
                        ? "bg-[#136bce] text-white shadow-sm ring-1 ring-[#136bce]"
                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                  >
                    {page}
                  </button>
                </React.Fragment>
              ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FabricInspectionReports;
